/**
 * /app/$tenantSlug/settings/hiring-emails — Settings → Hiring →
 * Candidate emails (HR-admin). Wave K1.3.
 *
 * Two surfaces:
 *  - Automated emails: per-tenant trigger toggles (application
 *    received / interview scheduled / offer sent). The REJECTION email
 *    is deliberately NOT a toggle — it is a per-reject human choice in
 *    the pipeline's reject dialog (Greenhouse-style human-in-the-loop).
 *  - Templates: the per-kind editor (name / subject / body / active)
 *    with a sample-variable preview. {{vars}} substitute SERVER-side
 *    at send time; the preview here is a client-side illustration with
 *    sample values.
 *
 * The backend lazily seeds the 6 system templates, so the list is
 * never empty for a healthy tenant. Catalog admin lives in the
 * Settings rail per SOP §8.3 (NOT in the hiring module strip).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { toApiError } from "@/lib/api";
import {
  useEmailTemplates,
  useHiringEmailSettings,
  useUpdateEmailTemplate,
  useUpdateHiringEmailSettings,
} from "@/hooks/use-hiring";
import type { EmailTemplate } from "@/types/hiring";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  SegmentedControl,
  type SegmentOption,
} from "@/components/shared/segmented-control";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/hiring-emails",
)({
  component: SettingsHiringEmailsRoute,
});

// Human labels for the system kinds (custom rows show their own name).
const EDIT_PREVIEW_OPTIONS: readonly SegmentOption<"edit" | "preview">[] = [
  { value: "edit", label: "Edit" },
  { value: "preview", label: "Preview" },
];

const KIND_LABELS: Record<string, string> = {
  application_received: "Application Received",
  stage_moved: "Application Update (Stage Moved)",
  interview_scheduled: "Interview Scheduled",
  interview_reminder: "Interview Reminder",
  rejection: "Application Closed (Rejection)",
  offer_sent: "Offer Extended",
  custom: "Custom",
};

// Sample values for the client-side PREVIEW only — real substitution
// happens server-side at send time with the candidate's actual data.
const SAMPLE_VARS: Record<string, string> = {
  candidate_name: "Priya Sharma",
  role: "Backend Engineer",
  company: "Your Company Pvt Ltd",
  interview_time: "Monday, 22 Jun 2026, 3:00 PM IST",
  status_link: "https://workright.in/status/…",
};

function previewSubstitute(s: string): string {
  let out = s;
  for (const [k, v] of Object.entries(SAMPLE_VARS)) {
    out = out.split(`{{${k}}}`).join(v);
  }
  return out;
}

function TemplateEditDialog({
  tpl,
  onClose,
}: Readonly<{ tpl: EmailTemplate; onClose: () => void }>) {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body_text);
  const [showPreview, setShowPreview] = useState(false);
  const updateMut = useUpdateEmailTemplate();

  async function onSave() {
    try {
      await updateMut.mutateAsync({
        id: tpl.id,
        body: { subject: subject.trim(), body_text: body.trim() },
      });
      toast.success("Template saved");
      onClose();
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Edit — {KIND_LABELS[tpl.kind] ?? tpl.name}
          </DialogTitle>
          <DialogDescription>
            Plain text. <code className="text-xs">{"{{candidate_name}}"}</code>,{" "}
            <code className="text-xs">{"{{role}}"}</code>,{" "}
            <code className="text-xs">{"{{company}}"}</code> and{" "}
            <code className="text-xs">{"{{interview_time}}"}</code> are
            filled in automatically when the email is sent.
            {tpl.kind === "application_received" && (
              <>
                {" "}
                <code className="text-xs">{"{{status_link}}"}</code> is the
                candidate&apos;s private link to check their application
                (added at the end if you leave it out).
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {/* Edit / Preview switch above the form — not in the footer, where it
            ended up alone under Cancel + Save on phones. */}
        <SegmentedControl
          label="Edit or preview the email"
          options={EDIT_PREVIEW_OPTIONS}
          value={showPreview ? "preview" : "edit"}
          onChange={(v) => setShowPreview(v === "preview")}
        />
        {showPreview ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Preview with sample values — actual emails use the
              candidate&apos;s real details.
            </p>
            <div className="rounded-md border p-3">
              <p className="text-sm font-medium">
                {previewSubstitute(subject)}
              </p>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                {previewSubstitute(body)}
              </pre>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-subject">Subject *</Label>
              <Input
                id="tpl-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-body">Body *</Label>
              <Textarea
                id="tpl-body"
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
          </div>
        )}
        <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={updateMut.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                updateMut.isPending ||
                subject.trim() === "" ||
                body.trim() === ""
              }
              onClick={onSave}
            >
              {updateMut.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AutomatedTogglesCard() {
  const settingsQ = useHiringEmailSettings();
  const updateMut = useUpdateHiringEmailSettings();
  const s = settingsQ.data?.settings;

  async function toggle(
    key:
      | "auto_application_received"
      | "auto_interview_scheduled"
      | "auto_offer_sent",
    value: boolean,
  ) {
    try {
      await updateMut.mutateAsync({ [key]: value });
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const rows: {
    key:
      | "auto_application_received"
      | "auto_interview_scheduled"
      | "auto_offer_sent";
    label: string;
    hint: string;
  }[] = [
    {
      key: "auto_application_received",
      label: "Application Received",
      hint: "Acknowledge every new careers-page application automatically.",
    },
    {
      key: "auto_interview_scheduled",
      label: "Interview Scheduled",
      hint: "Email the candidate when an interview round is scheduled.",
    },
    {
      key: "auto_offer_sent",
      label: "Offer Sent",
      hint: "Email the candidate when their offer is sent.",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Automated Emails</CardTitle>
        <CardDescription>
          Sent automatically using the templates below. Rejection emails
          are never automatic — they&apos;re a per-candidate choice in
          the reject dialog. Candidates without consent on record are
          never emailed (recorded as suppressed).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {s == null ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          rows.map((r) => (
            <div key={r.key} className="relative flex items-start gap-2 pointer-coarse:-mx-3.5 pointer-coarse:px-3.5 pointer-coarse:py-3.5">
              <Checkbox
                id={r.key}
                checked={s[r.key]}
                disabled={updateMut.isPending}
                onCheckedChange={(v) => void toggle(r.key, v === true)}
              />
              <div>
                <Label htmlFor={r.key} className="cursor-pointer after:absolute after:inset-0 after:content-['']">
                  {r.label}
                </Label>
                <p className="text-xs text-muted-foreground">{r.hint}</p>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TemplatesCard() {
  const templatesQ = useEmailTemplates();
  const updateMut = useUpdateEmailTemplate();
  const [editing, setEditing] = useState<EmailTemplate | null>(null);

  async function toggleActive(tpl: EmailTemplate, active: boolean) {
    try {
      await updateMut.mutateAsync({
        id: tpl.id,
        body: { is_active: active },
      });
      toast.success(active ? "Template activated" : "Template deactivated");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Templates</CardTitle>
        <CardDescription>
          One template per email kind. Deactivating a template switches
          that email off entirely (automated and template-picked sends).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {templatesQ.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : templatesQ.isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load templates.
          </p>
        ) : (
          <ul className="divide-y">
            {(templatesQ.data?.templates ?? []).map((tpl) => (
              <li
                key={tpl.id}
                className="flex flex-wrap items-center justify-between gap-2 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {KIND_LABELS[tpl.kind] ?? tpl.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground" title={tpl.subject}>
                    {tpl.subject}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {tpl.is_active ? (
                    <StatusBadge label="Active" tone="success" />
                  ) : (
                    <StatusBadge label="Off" tone="neutral" />
                  )}
                  <div className="relative flex items-center gap-1.5 pointer-coarse:-mx-3.5 pointer-coarse:min-h-11 pointer-coarse:px-3.5">
                    <Checkbox
                      id={`tpl-active-${tpl.id}`}
                      checked={tpl.is_active}
                      disabled={updateMut.isPending}
                      onCheckedChange={(v) =>
                        void toggleActive(tpl, v === true)
                      }
                    />
                    <Label
                      htmlFor={`tpl-active-${tpl.id}`}
                      className="cursor-pointer text-xs text-muted-foreground after:absolute after:inset-0 after:content-['']"
                    >
                      Active
                    </Label>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setEditing(tpl)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {editing && (
        <TemplateEditDialog
          // key forces fresh local state per template
          key={editing.id}
          tpl={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function SettingsHiringEmailsRoute() {
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Hiring Emails</h2>
            <p className="text-muted-foreground text-sm">
              Candidate-facing email templates and automated triggers.
              Every send is recorded on the candidate&apos;s
              Communication timeline.
            </p>
          </div>
          <AutomatedTogglesCard />
          <TemplatesCard />
        </div>
      </SettingsShell>
    </>
  );
}
